<template>
  <div class="container-fluid py-4">
    <!--
      No page title here. Every admin screen now leads with its own breadcrumb and
      heading, so a shell-level title rendered the screen name twice — once above
      the breadcrumb and again below it.
    -->

    <!--
      Admin screens are child routes rendered here; navigation lives in the
      sidebar (see useAdminNav) so every screen is deep-linkable.
    -->
    <router-view />
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAdminNav } from '@/composables/useAdminNav';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { canAccessAdminPanel, defaultAdminPath } = useAdminNav();

const currentUser = computed(() => store.getters['auth/currentUser']);

/**
 * /admin itself renders nothing — send the user to the first screen their
 * permissions allow. Anyone with no admin screen at all goes back to /profile.
 */
const redirectToDefault = () => {
  if (route.path !== '/admin') return;

  if (!canAccessAdminPanel.value || !defaultAdminPath.value) {
    store.dispatch('notify', {
      message: t('common.notification.unauthorized'),
      type: 'error'
    });
    router.replace('/profile');
    return;
  }

  router.replace(defaultAdminPath.value);
};

onMounted(() => {
  if (!currentUser.value?.isSuperAdmin) {
    store.dispatch('notify', {
      message: t('common.notification.unauthorized'),
      type: 'error'
    });
    router.push('/profile');
    return;
  }

  redirectToDefault();
});

// Navigating back to the bare /admin path (e.g. via the sidebar) re-resolves
// the landing screen instead of showing an empty shell.
watch(() => route.path, redirectToDefault);
</script>
